<script setup lang="ts">
  import SearchIcon from '../Icons/SearchIcon.vue'
  import YahooIcon from '../Icons/YahooIcon.vue'
  import CloseIcon from '../Icons/CloseIcon.vue'
  import SearchSuggestionItem from './SearchSuggestionItem.vue'
  import SearchBoxScrim from '../SearchBoxVariant/SearchBoxScrim.vue'

  const query = defineModel<string>()
  const searchInput = ref<string>()
  const isSuggestionsVisible = ref(false)
  const selectedIndex = ref(-1)
  const isSticky = ref(false)
  const searchboxRef = ref<HTMLElement | null>(null)
  const { t } = useI18n()

  const engagement = userEngagementStore()

  const isInputFocused = ref(false)
  const isScrimVisible = computed(
    () => isInputFocused.value && props.searchBoxVariant === 'scrim'
  )
  function activateScrim() {
    isInputFocused.value = true
  }

  const isGradientActive = computed(() => props.searchBoxVariant === 'gradient')

  onMounted(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        isSticky.value = entry.intersectionRatio < 1
      },
      { threshold: 1 }
    )

    if (searchboxRef.value instanceof HTMLElement) {
      observer.observe(searchboxRef.value)
    }
    if (!query.value && props.initialQuery) {
      query.value = props.initialQuery
    }
  })

  export type SearchboxProps = {
    display?: 'onImage' | 'solid'
    variant?: 'default' | 'v2' | 'v2-big-yolo' | 'v3'
    placeholder?: string
    yahooIconPosition?: 'none' | 'left' | 'right'
    searchBoxVariant?: string
    autofocus?: boolean
    suggestionHeader?: string
    suggestions?: SearchSuggestions
    sticky?: boolean
    initialQuery?: string
    topSearch?: boolean
  }

  const props = withDefaults(defineProps<SearchboxProps>(), {
    display: 'solid',
    variant: 'default',
    placeholder: '',
    yahooIconPosition: 'none',
    searchBoxVariant: 'off',
    autofocus: false,
    sticky: true,
    suggestionHeader: '',
    suggestions: () => [],
    initialQuery: '',
    topSearch: false,
  })

  watch(
    [() => props.suggestions, () => query.value],
    ([newSuggestions, newQuery]: [SearchSuggestions, typeof query.value]) => {
      if (newQuery && newSuggestions.length === 0) {
        hideSuggestions()
      } else if (!newQuery && newSuggestions.length === 0) {
        hideSuggestions()
      } else if (newQuery) {
        showSuggestions()
      }
    },
    { immediate: true }
  )

  watch(
    () => props.initialQuery,
    (newVal) => {
      query.value = newVal ?? ''
    }
  )

  type SearchboxEmits = {
    (event: 'clickSuggestionItem', query: Suggestion): void
    (
      event:
        | 'pressEnterOnSearchInput'
        | 'updateSuggestions'
        | 'pressSearchButton',
      query: string
    ): void
  }

  const emit = defineEmits<SearchboxEmits>()

  function hideSuggestions() {
    isSuggestionsVisible.value = false
  }
  function showSuggestions() {
    isSuggestionsVisible.value = true
  }
  function resetSelectedSuggestionIndex() {
    selectedIndex.value = -1
  }
  const handleClickSuggestion = (item: Suggestion) => {
    hideSuggestions()
    cleanSearchboxInput()
    emit('clickSuggestionItem', item)
  }

  function onEnter() {
    if (!query.value) return
    emit('pressEnterOnSearchInput', query.value)
    engagement.engage('search')
    hideSuggestions()
    if (props.initialQuery && props.initialQuery === query.value) return
    cleanSearchboxInput()
  }

  function onSearchButtonPress() {
    if (!query.value) return
    engagement.engage('search')
    hideSuggestions()
    cleanSearchboxInput()
    emit('pressSearchButton', query.value || '')
  }

  function handleKeyDown() {
    const suggestionSize = props.suggestions.length - 1
    if (selectedIndex.value === -1) {
      selectedIndex.value = 0
      query.value = props.suggestions[selectedIndex.value].text
    } else if (suggestionSize === selectedIndex.value) {
      resetSelectedSuggestionIndex()
    } else {
      selectedIndex.value++
      query.value = props.suggestions[selectedIndex.value].text
    }
  }

  function handleKeyUp() {
    if (selectedIndex.value === -1) {
      selectedIndex.value = props.suggestions.length - 1
    } else if (selectedIndex.value > 0) {
      selectedIndex.value--
    } else {
      resetSelectedSuggestionIndex()
    }

    query.value =
      selectedIndex.value === -1
        ? searchInput.value
        : props.suggestions[selectedIndex.value].text

    setTimeout(setCaretToEnd, 0)
  }

  function handleMouseOver(index: number) {
    selectedIndex.value = index
  }

  function handleMouseLeave() {
    resetSelectedSuggestionIndex()
  }

  function setCaretToEnd() {
    const inputElement = document.getElementById('searchInput')

    if (inputElement instanceof HTMLInputElement) {
      const inputLength = inputElement.value.length
      inputElement.setSelectionRange(inputLength, inputLength)
    }
  }

  function cleanSearchboxInput() {
    query.value = ''
    searchInput.value = ''
    resetSelectedSuggestionIndex()
    emit('updateSuggestions', query.value)
  }

  function openSuggestionsDropdown() {
    if (props.suggestions.length > 0) {
      showSuggestions()
    }
  }
  function onInputClick() {
    openSuggestionsDropdown()
    activateScrim()
  }
  function onInputBlur() {
    isInputFocused.value = false
  }
  function handleInputUpdate() {
    searchInput.value = query.value
    emit('updateSuggestions', query.value || '')
    activateScrim()
  }

  const searchIconPosition = computed(() =>
    props.yahooIconPosition === 'left' ? 'right' : 'left'
  )

  const strokeClass = computed(() => {
    if (
      props.variant === 'v2' ||
      props.variant === 'v2-big-yolo' ||
      props.variant === 'v3'
    ) {
      return 'stroke-action-primary-default group-hover:stroke-action-primary-hover group-active:stroke-action-primary-active'
    }
    return 'stroke-[#4B5563] dark:filter dark:invert'
  })

  const styleState = computed(() => {
    const style: {
      container?: string // Container searchbox lives in
      wrapper?: string // Wrapper for the searchbox
      input?: string // Input field
      iconSize?: number // Size of the search and close icons
    } = {}

    // Add bottom border to the container when searchbox is sticky
    if (props.sticky && isSticky.value) {
      style.container = 'border-border-interaction-neutral-normal border-b'
    }

    // DEFAULT variant
    if (props.variant === 'default') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-border-interaction-neutral-normal shadow-searchbox-hover hover:border-border-interaction-neutral-hover border'
          : ''
    }
    // V2 variant
    else if (props.variant === 'v2') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-action-primary-default shadow-searchbox-hover hover:border-action-primary-hover active:border-action-primary-active border-2'
          : ''
    }
    // V2-BIG-YOLO variant
    else if (props.variant === 'v2-big-yolo') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-action-primary-default shadow-searchbox-hover hover:border-action-primary-hover active:border-action-primary-active border-2 py-5'
          : 'py-5'

      style.iconSize = 24

      // Add fully rounded corners to the v2-big-yolo wrapper when suggestions are not visible
      if (!isSuggestionsVisible.value) {
        style.wrapper = `${style.wrapper} rounded-full`
      }
    }
    // V3 variant
    else if (props.variant === 'v3') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-action-primary-default shadow-searchbox-hover hover:border-action-primary-hover active:border-action-primary-active border-2 !pr-1 !py-1'
          : '!pr-1 !py-1'
    }

    if (props.topSearch) {
      const rounding = isSuggestionsVisible.value
        ? '!rounded-t-[28px] !rounded-b-none'
        : '!rounded-[100px]'

      style.wrapper = [
        props.display === 'solid'
          ? 'border-border-interaction-neutral-normal border'
          : '',
        'shadow-searchbox-default hover:shadow-searchbox-hover',
        'h-[56px] py-0 pl-6 pr-2',
        rounding,
      ]
        .filter(Boolean)
        .join(' ')

      style.iconSize = 20
    }

    // Remove border from wrapper when suggestions are visible
    if (isSuggestionsVisible.value) {
      style.wrapper = style.wrapper
        ? `${style.wrapper} rounded-b-none !border-transparent hover:!border-transparent`
        : 'rounded-b-none !border-transparent hover:!border-transparent'
    }

    // Input style
    if (props.variant === 'v2-big-yolo') {
      style.input = 'text-xl'
    } else {
      style.input = 'text-base'
    }

    return style
  })
</script>
<template>
  <div
    ref="searchboxRef"
    role="search"
    class="flex w-full justify-center bg-inherit"
    :class="[
      { 'sticky top-[-1px] z-20 py-4 px-2': sticky },
      styleState.container,
    ]"
  >
    <div
      v-click-outside="hideSuggestions"
      :class="
        topSearch
          ? 'w-full lg:w-[50%] max-w-[990px]'
          : ['w-full', { 'max-w-[768px]': sticky }]
      "
      class="relative"
    >
      <div
        class="group bg-background-default shadow-searchbox-default hover:shadow-searchbox-hover active:shadow-searchbox-hover relative z-[11] flex items-center gap-4 rounded-3xl px-6 py-3"
        :class="[
          styleState.wrapper,
          { 'searchbox-gradient': isGradientActive },
        ]"
      >
        <div class="flex flex-row justify-between flex-grow gap-4">
          <div
            class="flex flex-grow max-w-full items-center justify-start gap-4"
          >
            <SearchIcon
              v-if="searchIconPosition === 'left' && !topSearch"
              :size="styleState.iconSize"
              :svg-path-class="strokeClass"
            />
            <YahooIcon
              v-if="yahooIconPosition === 'left'"
              :size="styleState.iconSize"
            />
            <input
              id="searchInput"
              v-model.trim="query"
              data-testid="search-input"
              autocomplete="off"
              :autofocus="autofocus"
              :placeholder="t(placeholder)"
              class="flex flex-shrink placeholder-font-normal text-content-primary placeholder-content-secondary caret-content-primary dark:selection:text-content-primary w-full bg-inherit selection:bg-[#3367D1] selection:text-white focus:outline-none focus:placeholder-opacity-0"
              :class="styleState.input"
              @keydown.down="handleKeyDown"
              @keydown.up="handleKeyUp"
              @keyup.enter="onEnter"
              @click="onInputClick"
              @input="handleInputUpdate"
              @blur="onInputBlur"
            />
          </div>
          <div class="flex flex-row gap-8 items-center">
            <CloseIcon
              v-show="query"
              :size="styleState.iconSize"
              class="cursor-pointer"
              @click="cleanSearchboxInput"
            />
            <SearchIcon
              v-if="searchIconPosition === 'right' && !topSearch"
              :size="styleState.iconSize"
              :svg-path-class="strokeClass"
            />
            <YahooIcon
              v-if="yahooIconPosition === 'right' && !topSearch"
              :size="styleState.iconSize"
            />
            <ButtonsButton
              v-if="variant === 'v3' && !topSearch"
              size="small"
              rounded
              @click="onSearchButtonPress"
            >
              Search
            </ButtonsButton>
            <button
              v-if="topSearch"
              type="button"
              aria-label="Search"
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black transition-opacity hover:opacity-90"
              @click="onSearchButtonPress"
            >
              <SearchIcon :size="18" svg-path-class="stroke-white" />
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="isSuggestionsVisible"
        data-testid="suggestions"
        :class="{ 'shadow-searchbox-focus': isSuggestionsVisible }"
        class="bg-background-default-strong shadow-searchbox-default dark:border-border-interaction-neutral-normal absolute left-0 right-0 z-10 flex w-full flex-col overflow-x-hidden rounded-b-3xl pb-3 pt-1 dark:border-[1px]"
        @mouseleave="handleMouseLeave()"
      >
        <div
          v-if="suggestionHeader && suggestions && suggestions.length > 0"
          class="pb-2 pl-4 pt-3"
        >
          <Typography
            variant="text-xs"
            weight="font-semibold"
            color="text-content-secondary"
          >
            {{ suggestionHeader }}
          </Typography>
        </div>
        <ul>
          <SearchSuggestionItem
            v-for="(suggest, index) in suggestions"
            :key="
              'id' in suggest && typeof suggest.id === 'string'
                ? suggest.id
                : index
            "
            :searchbox-variant="variant"
            :text="suggest.text"
            :description="
              'description' in suggest &&
              typeof suggest.description === 'string'
                ? suggest.description
                : ''
            "
            :image="
              'image' in suggest && typeof suggest.image === 'string'
                ? suggest.image
                : ''
            "
            :icon="
              'icon' in suggest &&
              (suggest.icon === 'search' || suggest.icon === 'external-link')
                ? suggest.icon
                : 'search'
            "
            :query="query"
            :active="index === selectedIndex"
            @mouseover="handleMouseOver(index)"
            @click="handleClickSuggestion(suggest)"
          />
        </ul>
      </div>
    </div>
    <SearchBoxScrim :is-active="isScrimVisible" />
  </div>
</template>
<style scoped>
  :deep(ul) {
    padding-left: 0;
    margin-bottom: 0;
  }

  /* Variation B — animated gradient border around the search pill. */
  .searchbox-gradient {
    background: var(--colors-background);
    border: 2px solid transparent;
    background-image:
      linear-gradient(var(--colors-background), var(--colors-background)),
      linear-gradient(90deg, #26d0ce, #1e90ff, #1e40af, #3730a3, #26d0ce);
    background-origin: border-box;
    background-clip: padding-box, border-box;
    background-size:
      100% 100%,
      200% 100%;
    background-position:
      0 0,
      0 0;
    animation: gradient-shift 4s linear infinite;
    box-shadow:
      0 1px 2px rgba(38, 208, 206, 0.3),
      0 2px 4px rgba(30, 144, 255, 0.2),
      0 4px 8px rgba(0, 31, 127, 0.2),
      0 8px 16px rgba(26, 0, 102, 0.15);
  }
  .searchbox-gradient:hover {
    border: 2px solid rgb(37, 99, 235);
    animation: none;
    background-image: none;
    background-clip: border-box;
    background-color: var(--colors-background);
    box-shadow: 0 1px 2px rgba(27, 36, 44, 0.12);
    cursor: pointer;
  }
  @keyframes gradient-shift {
    from {
      background-position:
        0 0,
        0 0;
    }
    to {
      background-position:
        0 0,
        200% 0;
    }
  }
</style>
